import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {actions, selectors} from '../Redux/index';
import moment from 'moment';
import {formatNumber} from '../Utility/Format';
import {canSwapCcyPair} from '../Utility/CurrencyPairHelper'

import Icon from '../Components/Helper/Icon'
import ForwardTable from '../Components/Tables/ForwardTable';
import LastUpdateMessage from '../Components/Widgets/LastUpdateMessage';
import CurrencyChangeValue from '../Components/Widgets/CurrencyChangeValue';
import BigChart from '../Components/Widgets/BigChart';
import {ValueDateCalendarToggle} from './Application/ValueDateCalendar';
import CurrencyPairGranularitySelector from '../Components/Helper/CurrencyPairGranularitySelector';
import CurrencyPairLink, { formatCurrencyKey } from '../Components/Helper/CurrencyPairLink';
import StageActions from '../Components/Widgets/StageActions';
import {executeWebtrekk}from '../Utility/Webtrekk/WebtrekkMapp';

// if we only display one black number it should be the average of bid and ask
// information was  given by the customer
const average = (first, second) => {
    return (first + second) / 2;
};

const CurrencyPair = (props) => {
    
    const [searchParams] = useSearchParams();
    const currencyPair = searchParams.get('currencyPair');
    const navigate = useNavigate();

    const [currentGranularity, setCurrentGranularity] = useState({
        identifier: '1d',
        data: '1T',
        startTime: moment().subtract(3, 'months'),
        endTime: moment()
    });

    useEffect(() => {
        try {
            executeWebtrekk();
        } catch (error) {
            console.warn("Webtrekk execution failed:", error);
        }
        props.loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyPair]);


    const selectGranularity = (selectedGranularity) => {
        setCurrentGranularity(selectedGranularity);
    };

    const getDataDependingOnGranularity = (granularity, chartData) => {
        if (!chartData) {
            return [];
        }

        if (granularity.data === '1T') {
            return chartData['1T'] || [];
        } else {
            if (granularity.identifier === 'max') {
                return chartData['MAX'] || [];
            } else {
                const startTime = granularity.startTime.unix();
                const endTime = granularity.endTime.unix();
                return chartData['MAX'] ? chartData['MAX'].filter((chartDatum) => {
                    const timeStamp = moment(chartDatum.timestamp).unix();
                    return timeStamp >= startTime && timeStamp <= endTime;
                }) : [];
            }
        }
    };

    const getDurationOfChartData = (chartData) => {
        let duration = 0;
        if (!chartData) {
            duration = -1;
        } else {
            const days = chartData['MAX'] ? chartData['MAX'].length : 0;

            if (days >= 1) {
                const firstDate = moment(chartData['MAX'][0].timestamp);
                const lastDate = moment(chartData['MAX'][chartData['MAX'].length - 1].timestamp);
                duration = Math.abs(lastDate.diff(firstDate, 'days'));
            }
        }
        return duration;
    };

    const { data, currencies, locale } = props;

    if (!data.ccyPair) {
        return <LastUpdateMessage date={null} noData={data.hasOwnProperty('ccyPair')} />;
    }

    const chartData = getDataDependingOnGranularity(currentGranularity, data.chartData);
    const durationOfChartData = getDurationOfChartData(data.chartData);
    
    // Defensive check: Use the currencyPair from the URL for the swap check,
    // but also ensure it matches the pair in the currently loaded data.
    // This prevents the swap icon from being disabled during re-renders with stale data.
    const canSwap = (currencyPair === data.ccyPair) && canSwapCcyPair(currencyPair, currencies);

    return (
        <div className="currency-pair-details-screen">
            <div className="currency-pair-chart-outer">
                <div className="currency-pair-chart__header">
                    <CurrencyPairLink currencyPair={data.ccyPair} invertPair={canSwap}>
                        <Icon name="switch" isCollapsed={true} isInactive={!canSwap}/> {formatCurrencyKey(currencyPair)}
                    </CurrencyPairLink>

                    {data.spot ? (
                        <div className="metrics">
                            <CurrencyChangeValue value={data.spot.changeAbsolute} fractionDigits={4} showIcon={true} showText={true}/>
                            <CurrencyChangeValue value={data.spot.changePercent} fractionDigits={2} unit="%" showIcon={false} showText={true}/>
                            {formatNumber(average(data.spot.bid, data.spot.ask), 4)}
                        </div>
                    ) : null}
                </div>

                <BigChart chartData={chartData} granularity={currentGranularity.identifier} locale={locale} />

                <LastUpdateMessage date={data.lastUpdate}/>
            </div>

            <StageActions
                left={<CurrencyPairGranularitySelector currentGranularity={currentGranularity} onSelectGranularity={selectGranularity} durationOfChartData={durationOfChartData}/>}
                right={<ValueDateCalendarToggle onTogglePanel={props.onValuedateClick} />}
            />

            <div className="currency-pair-forward">
                <div className="currency-table__header">Forward</div>
                <ForwardTable
                    rows={data.forward?.tenors || []}
                    currencyPair={currencyPair}
                    invertPair={false}
                    locale={locale}
                />
                <LastUpdateMessage date={data.lastUpdate}/>
            </div>
        </div>
    );
}

const mapDispatchToProps = (dispatch) => {
    return {
        loadData: () => {
            dispatch(actions.Data.Reload.sagaReloadData());
        },
        onValuedateClick: () => {
            dispatch(actions.UI.OptionsMenu.toggleValueDateCalendar());
            dispatch(actions.UI.OptionsMenu.toggleOptionsMenu());
        }
    }
};

const mapStateToProps = (state) => {
    return {
        isValueDateVisible: selectors.UI.OptionsMenu.isValueDateVisible(state),
        data: selectors.Data.CurrencyPair.getData(state),
        currencies: selectors.Data.Currency.getCurrencies(state),
        locale: selectors.UI.Locale.getLocale(state)
    };
};

CurrencyPair.propTypes = {
    data: PropTypes.shape({
        ccyPair: PropTypes.string,
        forward: PropTypes.shape({
            ccyPair: PropTypes.string.isRequired,
            tenors: PropTypes.arrayOf(
                PropTypes.shape({
                    ask: PropTypes.number,
                    bid: PropTypes.number,
                    daysCount: PropTypes.number,
                    label: PropTypes.string,
                    mid: PropTypes.number,
                    valueDate: PropTypes.string,
                })
            )
        }),
        spot: PropTypes.shape({
            bid: PropTypes.number,
            ask: PropTypes.number,
            previousBid: PropTypes.number,
            previousAsk: PropTypes.number,
            changeAbsolute: PropTypes.number,
            changePercent: PropTypes.number,
            ccyPair: PropTypes.string,
            valueDate: PropTypes.string
        }),
        chartData: PropTypes.object
    }),
    currencies: PropTypes.array,
    locale: PropTypes.string
};

export default connect(mapStateToProps, mapDispatchToProps)(CurrencyPair);